import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, ProtectedRoute } from "@/features/auth";
import { ROUTES } from "@/lib/constants";
import { LoadingSpinner, SessionTimeoutWarning, ErrorBoundary, OfflineBanner } from "@/components/shared";

// Critical pages - eagerly loaded
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Pending from "@/pages/Pending";
import Dashboard from "@/pages/Dashboard";
import NotFound from "@/pages/NotFound";
import Welcome from "@/pages/Welcome";
import Marketing from "@/pages/Marketing";

// Lazy loaded pages - code splitting
const Progress = lazy(() => import("@/pages/Progress"));
const MyRecords = lazy(() => import("@/pages/MyRecords"));
const Profile = lazy(() => import("@/pages/Profile"));
const PolicyLibrary = lazy(() => import("@/pages/PolicyLibrary"));
const CertificateVerify = lazy(() => import("@/pages/CertificateVerify"));

// Admin Pages - lazy loaded
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminDepartments = lazy(() => import("@/pages/admin/Departments"));
const AdminModules = lazy(() => import("@/pages/admin/Modules"));
const AdminModuleDetail = lazy(() => import("@/pages/admin/ModuleDetail"));
const AdminLessonEditor = lazy(() => import("@/pages/admin/LessonEditor"));
const AdminPolicies = lazy(() => import("@/pages/admin/Policies"));
const AdminRecords = lazy(() => import("@/pages/admin/Records"));
const AdminComplianceSettings = lazy(() => import("@/pages/admin/ComplianceSettings"));
const AdminCompliance = lazy(() => import("@/pages/admin/Compliance"));

// Training Pages - lazy loaded
const Training = lazy(() => import("@/pages/Training"));
const ModuleView = lazy(() => import("@/pages/training/ModuleView"));
const LessonView = lazy(() => import("@/pages/training/LessonView"));
const AcceptLesson = lazy(() => import("@/pages/training/AcceptLesson"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Disable automatic refetch on window focus to prevent unnecessary API calls
      // when users switch tabs and return. This also helps preserve UI stability.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

// Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingSpinner />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <AuthProvider>
          <OfflineBanner />
          <SessionTimeoutWarning />
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Public routes */}
              <Route path={ROUTES.LOGIN} element={<Login />} />
              <Route path={ROUTES.REGISTER} element={<Register />} />
              <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
              <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
              
              {/* Certificate verification - PUBLIC, no auth required */}
              <Route path="/verify/:certificateId" element={<CertificateVerify />} />
              
              {/* Pending approval - requires auth but not approval */}
              <Route
                path={ROUTES.PENDING}
                element={
                  <ProtectedRoute requireApproval={false}>
                    <Pending />
                  </ProtectedRoute>
                }
              />
              
              {/* Protected routes */}
              <Route
                path={ROUTES.DASHBOARD}
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/progress"
                element={
                  <ProtectedRoute>
                    <Progress />
                  </ProtectedRoute>
                }
              />

              {/* Employee Training routes */}
              <Route
                path="/training"
                element={
                  <ProtectedRoute>
                    <Training />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training/:moduleId"
                element={
                  <ProtectedRoute>
                    <ModuleView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training/:moduleId/:lessonId"
                element={
                  <ProtectedRoute>
                    <LessonView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training/:moduleId/:lessonId/accept"
                element={
                  <ProtectedRoute>
                    <AcceptLesson />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.MY_RECORDS}
                element={
                  <ProtectedRoute>
                    <MyRecords />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.PROFILE}
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.POLICIES}
                element={
                  <ProtectedRoute>
                    <PolicyLibrary />
                  </ProtectedRoute>
                }
              />

              {/* Admin routes */}
              <Route
                path={ROUTES.ADMIN_DASHBOARD}
                element={
                  <ProtectedRoute requiredRoles={['super_admin', 'lesson_admin', 'client_admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ADMIN_USERS}
                element={
                  <ProtectedRoute requiredRoles={['super_admin', 'lesson_admin', 'client_admin']}>
                    <AdminUsers />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ADMIN_DEPARTMENTS}
                element={
                  <ProtectedRoute requiredRoles={['super_admin', 'client_admin']}>
                    <AdminDepartments />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ADMIN_MODULES}
                element={
                  <ProtectedRoute requiredRoles={['super_admin', 'lesson_admin', 'client_admin']}>
                    <AdminModules />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/modules/:moduleId"
                element={
                  <ProtectedRoute requiredRoles={['super_admin', 'lesson_admin', 'client_admin']}>
                    <AdminModuleDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/modules/:moduleId/lessons/:lessonId"
                element={
                  <ProtectedRoute requiredRoles={['super_admin', 'lesson_admin', 'client_admin']}>
                    <AdminLessonEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ADMIN_POLICIES}
                element={
                  <ProtectedRoute requiredRoles={['super_admin', 'lesson_admin', 'client_admin']}>
                    <AdminPolicies />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ADMIN_RECORDS}
                element={
                  <ProtectedRoute requiredRoles={['super_admin', 'lesson_admin', 'client_admin']}>
                    <AdminRecords />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings/compliance"
                element={
                  <ProtectedRoute requiredRoles={['super_admin']}>
                    <AdminComplianceSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/compliance"
                element={
                  <ProtectedRoute requiredRoles={['super_admin', 'lesson_admin', 'client_admin']}>
                    <AdminCompliance />
                  </ProtectedRoute>
                }
              />
              
              {/* Redirect /admin to /admin/dashboard */}
              <Route 
                path={ROUTES.ADMIN} 
                element={<Navigate to={ROUTES.ADMIN_DASHBOARD} replace />} 
              />
              
              {/* Default route - redirect to login for mobile app users */}
              <Route path={ROUTES.HOME} element={<Navigate to={ROUTES.LOGIN} replace />} />

              {/* Marketing page - accessible via direct URL if needed */}
              <Route path="/marketing" element={<Marketing />} />
              
              {/* Welcome page for employees (after org selection) */}
              <Route path={ROUTES.WELCOME} element={<Welcome />} />
              
              {/* 404 */}
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;